import React, { useState, useEffect, useMemo } from 'react';
import { 
  TeamOutlined, 
  UserAddOutlined, 
  HomeOutlined, 
  ClockCircleOutlined,
  FileTextOutlined,
  CrownOutlined,
  WarningOutlined,
  StopOutlined,
  DownOutlined,
  LoadingOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { message, Tag } from 'antd';
import axios from 'axios';
import { useAuth } from '../../../../../contexts/AuthContext';

// SMS segment templates (counts will be fetched from API)
const getSmsSegmentTemplates = (counts = {}) => [
  {
    id: 'opted-in',
    icon: <TeamOutlined />,
    iconBg: '#e6f7ff',
    iconColor: 'var(--ink)',
    title: 'All Opted-In Contacts',
    subtitle: 'Largest legally compliant group',
    count: counts.opted_in || 0,
  },
  {
    id: 'new-subscribers',
    icon: <UserAddOutlined />,
    iconBg: '#f0f5ff',
    iconColor: '#2f54eb',
    title: 'New Subscribers',
    subtitle: 'Last 30 days - welcome...',
    count: counts.new_subscribers || 0,
  },
  {
    id: 'active-members',
    icon: <HomeOutlined />,
    iconBg: '#fff7e6',
    iconColor: '#fa8c16',
    title: 'Active Members',
    subtitle: 'Service agreement holders',
    count: counts.active_members || 0,
  },
  {
    id: 'recent-service',
    icon: <ClockCircleOutlined />,
    iconBg: '#fff2e8',
    iconColor: '#fa541c',
    title: 'Recent Service',
    subtitle: 'Last 90 days - follow-up...',
    count: counts.recent_service || 0,
  },
  {
    id: 'open-estimates',
    icon: <FileTextOutlined />,
    iconBg: '#e6fffb',
    iconColor: '#13c2c2',
    title: 'Open Estimates',
    subtitle: 'Unsold quotes - sales recovery',
    count: counts.open_estimates || 0,
  },
  {
    id: 'high-ltv',
    icon: <CrownOutlined />,
    iconBg: '#fffbe6',
    iconColor: '#faad14',
    title: 'High LTV Customers',
    subtitle: 'Loyalty & premium offers',
    count: counts.high_ltv || 0,
  },
  {
    id: 'aged-equipment',
    icon: <WarningOutlined />,
    iconBg: '#fff1f0',
    iconColor: '#f5222d',
    title: 'Aged Equipment',
    subtitle: 'Replacement offer targets',
    count: counts.aged_equipment || 0,
  },
];

// Email segment templates (counts will be fetched from API)
const getEmailSegmentTemplates = (counts = {}) => [
  {
    id: 'all-email-subscribers',
    icon: <TeamOutlined />,
    iconBg: '#e6f7ff',
    iconColor: 'var(--ink)',
    title: 'All Email Subscribers',
    subtitle: 'Largest legally compliant group',
    count: counts.all_email_subscribers || 0,
  },
  {
    id: 'new-email-subscribers',
    icon: <UserAddOutlined />,
    iconBg: '#f0f5ff',
    iconColor: '#2f54eb',
    title: 'New Email Subscribers',
    subtitle: 'Last 30 days - welcome emails',
    count: counts.new_email_subscribers || 0,
  },
  {
    id: 'active-members',
    icon: <HomeOutlined />,
    iconBg: '#fff7e6',
    iconColor: '#fa8c16',
    title: 'Active Members',
    subtitle: 'Service agreement holders',
    count: counts.active_members || 0,
  },
  {
    id: 'recent-service',
    icon: <ClockCircleOutlined />,
    iconBg: '#fff2e8',
    iconColor: '#fa541c',
    title: 'Recent Service',
    subtitle: 'Last 90 days - follow-up...',
    count: counts.recent_service || 0,
  },
  {
    id: 'open-estimates',
    icon: <FileTextOutlined />,
    iconBg: '#e6fffb',
    iconColor: '#13c2c2',
    title: 'Open Estimates',
    subtitle: 'Unsold quotes - sales recovery',
    count: counts.open_estimates || 0,
  },
  {
    id: 'high-ltv',
    icon: <CrownOutlined />,
    iconBg: '#fffbe6',
    iconColor: '#faad14',
    title: 'High LTV Customers',
    subtitle: 'Loyalty & premium offers',
    count: counts.high_ltv || 0,
  },
  {
    id: 'aged-equipment',
    icon: <WarningOutlined />,
    iconBg: '#fff1f0',
    iconColor: '#f5222d',
    title: 'Aged Equipment',
    subtitle: 'Replacement offer targets',
    count: counts.aged_equipment || 0,
  },
];

const SelectTargetSegments = ({ selectedSegments = [], onUpdate, onNext, channel, campaignData = {} }) => {
  const uploadedCustomers = campaignData.uploadedCustomers || [];
  const hasUploadedCustomers = uploadedCustomers.length > 0;
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [segmentCounts, setSegmentCounts] = useState({});
  const isEmail = channel === 'email';

  // Fetch segment counts from API
  useEffect(() => {
    const fetchSegmentCounts = async () => {
      setLoading(true);
      try {
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Fetch CRM stats for total customer count
        const statsResponse = await axios.get('/api/crm/stats', { headers: authHeaders });
        const totalCustomers = statsResponse.data?.totalCustomers || 0;
        
        let newSubscriberCount = 0;
        let customers = [];
        
        // Try to fetch customer list to calculate new subscribers
        try {
          const customersResponse = await axios.get('/api/crm/customers', { headers: authHeaders });
          customers = Array.isArray(customersResponse.data) ? customersResponse.data : [];
          
          newSubscriberCount = customers.filter(c => {
            if (!c.created_at) return false;
            const created = new Date(c.created_at);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            return created >= thirtyDaysAgo;
          }).length;
        } catch (customerError) {
          console.warn('Could not fetch customer list, using estimates:', customerError);
          // Estimate new subscribers as 7% of total if we can't fetch the list
          newSubscriberCount = Math.floor(totalCustomers * 0.07);
        }
        
        // Calculate segment counts based on real customer data
        const counts = {
          opted_in: totalCustomers,
          new_subscribers: newSubscriberCount,
          active_members: customers.filter(c => c.is_active_member).length,
          recent_service: customers.filter(c => c.last_service_date).length,
          open_estimates: customers.filter(c => c.has_open_estimate).length,
          high_ltv: customers.filter(c => c.is_high_ltv).length,
          aged_equipment: customers.filter(c => c.equipment_age_years > 10).length,
          all_email_subscribers: totalCustomers,
          new_email_subscribers: newSubscriberCount,
        };
        
        setSegmentCounts(counts);
      } catch (error) {
        console.error('Error fetching segment counts:', error);
        const errorMsg = error?.response?.data?.error || error.message || 'Failed to load audience segments';
        message.error(errorMsg);
        
        // Set minimal default counts so user can still proceed
        setSegmentCounts({
          opted_in: 0,
          new_subscribers: 0,
          active_members: 0,
          recent_service: 0,
          open_estimates: 0,
          high_ltv: 0,
          aged_equipment: 0,
          all_email_subscribers: 0,
          new_email_subscribers: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSegmentCounts();
  }, [token]);

  // Use channel-specific segments with real counts
  const segments = useMemo(() => {
    const base = channel === 'email' 
      ? getEmailSegmentTemplates(segmentCounts)
      : getSmsSegmentTemplates(segmentCounts);
    // Prepend CSV Upload segment if customers were uploaded
    if (hasUploadedCustomers) {
      return [{
        id: 'csv-upload',
        icon: <UploadOutlined />,
        iconBg: '#f0fdf4',
        iconColor: '#22c55e',
        title: 'Uploaded CSV Contacts',
        subtitle: 'Your uploaded customer list',
        count: uploadedCustomers.length,
        isCsv: true,
      }, ...base];
    }
    return base;
  }, [channel, segmentCounts, hasUploadedCustomers, uploadedCustomers.length]);

  const toggleSegment = (segmentId) => {
    const isSelected = selectedSegments.includes(segmentId);
    if (isSelected) {
      onUpdate({ selectedSegments: selectedSegments.filter(id => id !== segmentId) });
    } else {
      onUpdate({ selectedSegments: [...selectedSegments, segmentId] });
    }
  };

  const estimatedReach = useMemo(() => {
    return segments
      .filter(s => selectedSegments.includes(s.id))
      .reduce((sum, s) => sum + s.count, 0);
  }, [selectedSegments, segments]);

  const handleContinue = () => {
    if (selectedSegments.length > 0) {
      onNext();
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="step-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <LoadingOutlined style={{ fontSize: 48, color: 'var(--ink)', marginBottom: 16 }} spin />
          <p style={{ color: '#8c8c8c' }}>Loading audience segments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-container">
      <h1 className="step-title">
        Select Your {isEmail ? 'Email ' : ''}Target Segments
      </h1>
      <p className="step-subtitle">
        Choose one or more pre-built audience segments for your {isEmail ? 'email ' : ''}campaign
      </p>

      {/* Exclusion List Banner */}
      <div className="exclusion-banner">
        <div className="exclusion-icon">
          <StopOutlined />
        </div>
        <div className="exclusion-content">
          <strong>{isEmail ? 'Unsubscribed Contacts Excluded' : 'Exclusion List (Do Not Message)'}</strong>
          <span className="exclusion-subtitle">
            Automatically applied for {isEmail ? 'CAN-SPAM' : 'TCPA'} compliance
          </span>
        </div>
        <span className="exclusion-status">Always Active</span>
      </div>

      {/* Segments Grid */}
      <div className="segments-grid">
        {segments.map((segment) => {
          const isSelected = selectedSegments.includes(segment.id);
          return (
            <div
              key={segment.id}
              className={`segment-card ${isSelected ? 'selected' : ''}`}
              onClick={() => toggleSegment(segment.id)}
            >
              <div 
                className="segment-icon"
                style={{ background: segment.iconBg }}
              >
                {React.cloneElement(segment.icon, { style: { color: segment.iconColor } })}
              </div>
              <div className="segment-title">{segment.title}</div>
              <div className="segment-subtitle">{segment.subtitle}</div>
              <div className="segment-count">
                {segment.count.toLocaleString()}
                {segment.isCsv && <Tag color="green" style={{ marginLeft: 6, fontSize: 11 }}>CSV</Tag>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Estimated Reach */}
      <div className="estimated-reach">
        <TeamOutlined />
        <span>Estimated Reach: <strong>{estimatedReach.toLocaleString()}</strong> contacts</span>
      </div>

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info">
          {selectedSegments.length > 0 
            ? `${selectedSegments.length} segment${selectedSegments.length > 1 ? 's' : ''} selected`
            : 'Select segments'
          }
        </div>
        <button 
          className="continue-btn" 
          onClick={handleContinue}
          disabled={selectedSegments.length === 0}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default SelectTargetSegments;
